import { collection, CollectionReference, doc, setDoc, updateDoc } from "firebase/firestore";
import { observer } from "mobx-react";
import { useFirestore, useFirestoreCollectionData, useFirestoreDocData } from "reactfire";
import { SimpleButton } from "../UI/SimpleButton";
import React, { useEffect, useRef } from "react";
import { v4 as uuidv4 } from "uuid";
import { logDebug, logError } from "../LocalLogger";
import { appStore, printifyRequest } from "../utils/AppMobx";
import TopMostPortal from "../Portals";
import { PreviewImage } from "../UI/ClickableImage";
import UploadImageButton from "../utils/UploadImageButton";
import printifyService from "../utils/PrintifyService";
import { Utils } from "../utils/utils";
import { fixProduct, getColorsMap, ProductType } from "./productTypes";
import { ProviderId } from "firebase/auth";
import { localLargeStorage } from "../utils/LocalLargeStorage";
import { alwaysFilter, createProductCreation as createProductCreationPayload, PreviewPage } from "./PreviewPage";

export interface IVariantSimple {
	id: number;
	decoration_methods: string[];
	options: {
		color: string;
		size: string;
		[key: string]: any;
	};
	placeholders: {
		id?: string;
		position: string;
		width: number;
		height: number;
	}[];
}

interface IConceptDesignImage {
	id: string;
	url: string;
	position: string;
	placeholderId: string;
	filename: string;
}

export interface IConceptDesign {
	name: string;
	id: string;
	images: IConceptDesignImage[];
	selectedColors?: string[];
}
export interface IConceptBlueprint {
	storedProductId: string;
	id: number;
	title: string;
	description?: string;
	designs: IConceptDesign[];
	prices?: { [key: number]: number };
	tags?: string[];
	otherFilters?: { type: string; id: number; value: string }[];
}

interface IConcept {
	name: string;
	id?: string;
	bluprints: IConceptBlueprint[];
}

const PriceAndTagsSelector: React.FC<{
	conceptId: string;
	blueprintId: number;
	designId: string;
	product: ProductType;
}> = ({ conceptId, blueprintId, designId, product }) => {
	const firestore = useFirestore();
	const conceptsRef = collection(firestore, "/store/site/concepts") as CollectionReference<IConcept>;
	const conceptsCD = useFirestoreCollectionData<IConcept>(conceptsRef, { idField: "id" });
	const [showAddExistingTags, setShowAddExistingTags] = React.useState<boolean>(false);
	const concept = conceptsCD.data.find((c) => c.id === conceptId);
	const conceptBlueprint = concept?.bluprints.find((bp) => bp.id === blueprintId);
	conceptBlueprint!.prices = conceptBlueprint!.prices || {};
	const design = conceptBlueprint?.designs.find((d) => d.id === designId);
	const settingsRef = doc(firestore, "/store/site/settings/siteSettings");
	const settingsData = useFirestoreDocData(settingsRef);
	const [shippingPrice, setShippingPrice] = React.useState<any[]>();
	const [prices, setPrices] = React.useState<Record<number, number>>(conceptBlueprint!.prices);
	const [title, setTitle] = React.useState<string>(conceptBlueprint!.title || "");
	const [description, setDescription] = React.useState<string>(conceptBlueprint!.description || "");
	const printifyBlueprint = appStore.selectedUKProviderBlueprints.find((bp) => bp.id === blueprintId);
	useEffect(() => {
		async function fetchShippingPrice() {
			if (!printifyBlueprint) {
				return;
			}
			appStore.showLoading = true;
			try {
				const price = await printifyRequest("getShippingRates", {
					blueprintId: printifyBlueprint.id,
					providerId: appStore.selectedProviderId,
				});
				logDebug("Shipping price fetched:", price);

				setShippingPrice(price.profiles || []);
			} catch (err) {
				logError("Error fetching shipping price:", err);
			}
			appStore.showLoading = false;
		}
		fetchShippingPrice();
	}, [printifyBlueprint, appStore.selectedProviderId]);

	if (
		(!shippingPrice && settingsData.status === "loading") ||
		conceptsCD.status === "loading" ||
		!concept ||
		!conceptBlueprint ||
		!design
	) {
		return <div>Loading data...</div>;
	}

	const maxShippingPrice =
		shippingPrice?.reduce((max, profile) => {
			const profileMax = Math.max(max, profile.first_item.cost, profile.additional_items.cost);

			return Math.max(max, profileMax);
		}, 0) || -100;

	return (
		<div>
			<div className="mt-2 text-xl text-green-800">Prices: (Max shipping price {maxShippingPrice / 100})</div>
			{product.per_cost?.map((variant) => (
				<div key={variant.id} className="mb-2 flex gap-2 items-center">
					<h4 className="font-semibold">{variant.title}</h4>
					{/* <p>Price: ${variant.price / 100}</p> */}
					<p>Cost: ${variant.cost / 100}</p>
					<input
						type="text"
						className="border p-1 rounded"
						placeholder="price"
						value={prices[variant.cost] || ""}
						onChange={(e) => {
							const newPrice = parseFloat(e.target.value);
							if (!newPrice || !isNaN(newPrice)) {
								setPrices((prev) => ({
									...prev,
									[variant.cost]: newPrice,
								}));
							}
						}}
						onBlur={async () => {
							appStore.showLoading = true;
							try {
								conceptBlueprint!.prices = { ...prices };
								await setDoc(doc(conceptsRef, conceptId), { ...concept });
							} catch (err) {}
							appStore.showLoading = false;
						}}
					/>
				</div>
			)) || <p>No variants available for this product.</p>}
			<div className="mt-2 text-xl text-green-800">TAGS:</div>
			{conceptBlueprint.tags?.map((tag) => (
				<span
					key={tag}
					className="bg-gray-200 px-2 py-1 rounded mr-2 mb-2 inline-block cursor-pointer"
					onClick={async () => {
						conceptBlueprint.tags = conceptBlueprint.tags!.filter((t) => t !== tag);
						appStore.showLoading = true;
						try {
							await setDoc(doc(conceptsRef, conceptId), { ...concept });
						} catch (err) {}
						appStore.showLoading = false;
					}}
				>
					{tag} <span className="text-red-500">x</span>
				</span>
			))}
			<input
				type="text"
				className="border p-1 rounded mb-2"
				placeholder="Add category (Enter)"
				onKeyDown={async (e) => {
					if (e.key === "Enter") {
						const newCategory = (e.target as HTMLInputElement).value.trim();
						conceptBlueprint.tags = conceptBlueprint.tags || [];
						if (newCategory && !conceptBlueprint.tags.includes(newCategory)) {
							conceptBlueprint.tags.push(newCategory);
							appStore.showLoading = true;
							try {
								await setDoc(doc(conceptsRef, conceptId), { ...concept });
								settingsData.data.tags = settingsData?.data?.tags || [];
								settingsData.data.tags.push(newCategory);
								settingsData.data.tags = Array.from(new Set(settingsData.data.tags));
								await setDoc(settingsRef, { ...settingsData.data });
								logDebug("New category added:", newCategory, settingsData.data.tags);
							} catch (err) {
								logError("Error adding new category:", err);
							}
							appStore.showLoading = false;

							(e.target as HTMLInputElement).value = ""; // Clear input
						}
					}
				}}
			/>
			<button onClick={() => setShowAddExistingTags(true)} className="bg-blue-500 text-white px-2 py-1 rounded m-2">
				Add Existing
			</button>
			<div className="flex">
				<div className="text-sm text-gray-500">Title:</div>
				<input
					type="text"
					className=" border w-[600px]"
					onChange={(e) => {
						setTitle(e.target.value);
					}}
					value={title}
					placeholder="Enter title"
					onBlur={async () => {
						appStore.showLoading = true;
						try {
							conceptBlueprint.title = title;
							await setDoc(doc(conceptsRef, conceptId), { ...concept });
						} catch (err) {
							logError("Error saving title:", err);
						}
						appStore.showLoading = false;
					}}
				></input>
				<button
					onClick={async () => {
						if (!settingsData?.data?.titlePrompt) {
							alert("Please set the title prompt in settings first.");
							return;
						}
						appStore.showLoading = true;
						try {
							// <ITEM_NAME>
							// <ITEM_TYPE>
							// <ITEM_CATS>
							const itemName = concept.name;
							const itemType = printifyBlueprint!.title;
							const itemCats = conceptBlueprint!.tags?.join(", ") || "";
							const prompt = (settingsData!.data!.titlePrompt as string)
								.replaceAll("<ITEM_NAME>", itemName)
								.replaceAll("<ITEM_TYPE>", itemType)
								.replaceAll("<ITEM_CATS>", itemCats);
							const _title = await printifyRequest("openAICall", { imagesBase64: [], prompt: prompt });
							if (_title) {
								let cleanTitle = _title;
								if (cleanTitle.startsWith('"')) {
									cleanTitle = cleanTitle.slice(1);
								}
								if (cleanTitle.endsWith('"')) {
									cleanTitle = cleanTitle.slice(0, -1);
								}
								logDebug("Title generated with AI: " + cleanTitle);
								setTitle(cleanTitle);
								conceptBlueprint.title = cleanTitle;
								await setDoc(doc(conceptsRef, conceptId), { ...concept });
							} else {
								alert("Failed to generate title with AI.");
							}
						} catch (err) {}
						appStore.showLoading = false;
					}}
				>
					Gen With AI
				</button>
			</div>
			<div className="flex">
				<div className="text-sm text-gray-500">Description:</div>
				<textarea
					value={description}
					onChange={(e) => {
						setDescription(e.target.value);
					}}
					onBlur={async () => {
						conceptBlueprint.description = description;
						appStore.showLoading = true;
						try {
							await setDoc(doc(conceptsRef, conceptId), { ...concept });
						} catch (err) {}
						appStore.showLoading = false;
					}}
					placeholder="Enter description"
					className=" border w-[600px]"
				/>
				<button
					onClick={async () => {
						if (!settingsData?.data?.descriptionPrompt) {
							alert("Please set the title prompt in settings first.");
							return;
						}
						appStore.showLoading = true;
						try {
							// <ITEM_NAME>
							// <ITEM_TYPE>
							// <ITEM_CATS>
							const itemName = concept.name;
							const itemType = printifyBlueprint!.title;
							const itemCats = conceptBlueprint!.tags?.join(", ") || "";
							const prompt = (settingsData!.data!.descriptionPrompt as string)
								.replaceAll("<ITEM_NAME>", itemName)
								.replaceAll("<ITEM_TYPE>", itemType)
								.replaceAll("<ITEM_CATS>", itemCats);
							const _title = await printifyRequest("openAICall", {
								imagesBase64: (design.images || []).map((i) => i.url),
								prompt: prompt,
							});
							if (_title) {
								let cleanTitle = _title;
								if (cleanTitle.startsWith('"')) {
									cleanTitle = cleanTitle.slice(1);
								}
								if (cleanTitle.endsWith('"')) {
									cleanTitle = cleanTitle.slice(0, -1);
								}
								logDebug("Title generated with AI: " + cleanTitle);
								setDescription(cleanTitle);
								conceptBlueprint.description = cleanTitle;
								await setDoc(doc(conceptsRef, conceptId), { ...concept });
							} else {
								alert("Failed to generate title with AI.");
							}
						} catch (err) {}
						appStore.showLoading = false;
					}}
				>
					Gen With AI
				</button>
			</div>
			{showAddExistingTags && (
				<TopMostPortal>
					<div className="bg-white p-4 rounded shadow-lg w-[400px] flex flex-col">
						<h3 className="text-lg font-semibold mb-2">Add Existing Tags</h3>
						<div style={{ display: "flex", flexWrap: "wrap" }}>
							{(settingsData.data.tags || [])
								.filter((tag: string) => !conceptBlueprint.tags?.includes(tag))
								.map((tag: string) => (
									<span
										key={tag}
										className="bg-gray-200 px-2 py-1 rounded mr-2 mb-2 inline-block cursor-pointer"
										onClick={async () => {
											if (!conceptBlueprint.tags) {
												conceptBlueprint.tags = [];
											}
											if (!conceptBlueprint.tags.includes(tag)) {
												conceptBlueprint.tags.push(tag);
												appStore.showLoading = true;
												try {
													await setDoc(doc(conceptsRef, conceptId), { ...concept });
												} catch (err) {}
												appStore.showLoading = false;
											}
										}}
									>
										{tag}
									</span>
								))}
						</div>
						<button className="mt-2 bg-blue-500 text-white px-4 py-2 rounded" onClick={() => setShowAddExistingTags(false)}>
							Close
						</button>
					</div>
				</TopMostPortal>
			)}
		</div>
	);
};
const OtherFilters: React.FC<{
	conceptId: string;
	blueprintId: number;
	designId: string;
	product: ProductType;
}> = ({ conceptId, blueprintId, designId, product }) => {
	// const [selectedFilters, setSelectedFilters] = React.useState<{ type: string; id: number }[]>([]);
	const firestore = useFirestore();
	const conceptsRef = collection(firestore, "/store/site/concepts") as CollectionReference<IConcept>;
	const conceptsCD = useFirestoreCollectionData<IConcept>(conceptsRef, { idField: "id" });
	const concept = conceptsCD.data.find((c) => c.id === conceptId);
	const conceptBlueprint = concept?.bluprints.find((bp) => bp.id === blueprintId);
	conceptBlueprint!.otherFilters = conceptBlueprint!.otherFilters || [];
	const design = conceptBlueprint?.designs.find((d) => d.id === designId);
	if (conceptsCD.status === "loading" || !concept || !conceptBlueprint || !design) {
		return <div>Loading data...</div>;
	}
	return (
		<div>
			<div className="mt-2 text-xl text-green-800">Other Filters:(selected will be excluded)</div>
			{product.options?.map((option) => {
				if (option.type === "color") {
					return null;
				}
				return (
					<div key={option.type} className="mb-2 ml-4">
						<h4 className="font-semibold">{option.name}</h4>
						<div className="flex gap-1">
							{option.values
								.filter((v) => v.used)
								.filter((v) => !alwaysFilter.includes(v.title.toLowerCase()))
								.map((value) => (
									<div
										key={value.id}
										className={`border p-1 rounded cursor-pointer ${
											conceptBlueprint!.otherFilters!.find((v) => v.id === value.id) ? "bg-red-500" : ""
										}`}
										onClick={async () => {
											if (conceptBlueprint!.otherFilters!.find((v) => v.id === value.id)) {
												conceptBlueprint!.otherFilters = conceptBlueprint!.otherFilters!.filter(
													(v) => v.id !== value.id
												);
											} else {
												conceptBlueprint!.otherFilters!.push({
													id: value.id,
													type: option.type,
													value: value.title,
												});
											}
											appStore.showLoading = true;
											try {
												await setDoc(doc(conceptsRef, conceptId), { ...concept });
											} catch (err) {}
											appStore.showLoading = false;
										}}
									>
										{value.type === "color" ? null : (
											<div className="border min-w-6 h-6 text-center">{value.title}</div>
										)}
									</div>
								))}
						</div>
					</div>
				);
			}) || <p>No options available for this product.</p>}
		</div>
	);
};

const ColorsSelector: React.FC<{
	colorMap: Record<string, string>;
	conceptId: string;
	blueprintId: number;
	designId: string;
	colors: string[];
	onColorSelected: (color: string) => void;
}> = ({ colorMap, colors, onColorSelected, blueprintId, conceptId, designId }) => {
	colors = colors.sort((a, b) => {
		// remove # and use first 2 chars for numeric value for comparison
		const aNum = parseInt(a.replace("#", "").substring(2, 4), 16);
		const bNum = parseInt(b.replace("#", "").substring(2, 4), 16);
		return aNum - bNum;
	});
	const firestore = useFirestore();
	const conceptsRef = collection(firestore, "/store/site/concepts") as CollectionReference<IConcept>;
	const conceptsCD = useFirestoreCollectionData<IConcept>(conceptsRef, { idField: "id" });
	const concept = conceptsCD.data.find((c) => c.id === conceptId);
	const conceptBlueprint = concept?.bluprints.find((bp) => bp.id === blueprintId);
	const design = conceptBlueprint?.designs.find((d) => d.id === designId);
	async function selectOneColor(color: string, select: boolean, updateDoc: boolean) {
		if (!design) {
			return;
		}
		if (!design!.selectedColors) {
			design!.selectedColors = [];
		}
		if (!select) {
			design!.selectedColors = design?.selectedColors?.filter((c) => c !== color);
		} else {
			if (!design!.selectedColors.includes(color)) {
				design!.selectedColors.push(color);
			}
			conceptBlueprint!.designs.forEach((d) => {
				if (d.id !== designId) {
					if (!d.selectedColors) {
						d.selectedColors = [];
					}
					d.selectedColors = d.selectedColors?.filter((c) => c !== color);
				}
			});
		}
		if (updateDoc) {
			await update();
		}
	}
	async function update() {
		appStore.showLoading = true;
		try {
			await setDoc(doc(conceptsRef, conceptId), { ...concept });
		} catch (err) {
			logDebug("Error saving selected colors:", err);
		}
		appStore.showLoading = false;
	}
	async function selectAllColors(select: "all" | "none" | "dark" | "light") {
		if (!design) {
			return;
		}
		if (select === "none" || select === "all") {
			for (const color of colors) {
				selectOneColor(color, select === "all", false);
			}
		} else if (select === "dark") {
			for (const color of darkColors) {
				selectOneColor(colorMap[color], true, false);
			}
			for (const color of lightColors) {
				selectOneColor(colorMap[color], false, false);
			}
		} else if (select === "light") {
			for (const color of darkColors) {
				selectOneColor(colorMap[color], false, false);
			}
			for (const color of lightColors) {
				selectOneColor(colorMap[color], true, false);
			}
		}
		await update();
	}

	return (
		<div className="flex gap-1">
			<button onClick={() => selectAllColors("all")} className="whitespace-nowrap bg-blue-500 text-white px-2 rounded">
				All
			</button>
			<button onClick={() => selectAllColors("none")} className="whitespace-nowrap bg-blue-500 text-white px-2 rounded">
				None
			</button>
			<button onClick={() => selectAllColors("dark")} className="whitespace-nowrap bg-blue-500 text-white px-2 rounded">
				Dark
			</button>
			<button onClick={() => selectAllColors("light")} className="whitespace-nowrap bg-blue-500 text-white px-2 rounded">
				Light
			</button>
			{colors.map((color) => (
				<div
					className="p-0.5 "
					style={{
						backgroundColor: design?.selectedColors?.includes(color) ? "#e0f7fa" : "transparent",
						border: design?.selectedColors?.includes(color) ? "1px solid #000" : "1px solid transparent",
						borderRadius: "4px",
						cursor: "pointer",
						// transition: "background-color 0.3s, border 0.3s",
					}}
				>
					<div
						key={color}
						className="cursor-pointer p-2 "
						style={{ backgroundColor: color }}
						onClick={() => {
							selectOneColor(color, !design?.selectedColors?.includes(color), true);
						}}
					>
						{/* {color} */}
					</div>
				</div>
			))}
		</div>
	);
};

const DesignEditor: React.FC<{
	variants: IVariantSimple[];
	conceptId: string;
	blueprintId: number;
	designId: string;
	placeholders: { position: string; width: number; height: number; id: string }[];
}> = observer(({ variants, conceptId, blueprintId, designId, placeholders }) => {
	const [selectedPlaceholder, setSelectedPlaceholder] = React.useState<string>(placeholders[0]?.id);
	const firestore = useFirestore();
	const useInAppBlueprintsRef = collection(firestore, "/store/site/concepts") as CollectionReference<IConcept>;
	const useInAppBlueprintsCD = useFirestoreCollectionData<IConcept>(useInAppBlueprintsRef, { idField: "id" });
	const concept = useInAppBlueprintsCD.data.find((c) => c.id === conceptId);
	const printifyBlueprint = appStore.selectedUKProviderBlueprints.find((bp) => bp.id === blueprintId);
	const conceptBlueprint = concept?.bluprints.find((bp) => bp.id === blueprintId);
	const design = conceptBlueprint?.designs.find((d) => d.id === designId);
	// logDebug("variants", variants);

	if (useInAppBlueprintsCD.status === "loading" || !concept || !conceptBlueprint || !printifyBlueprint || !design) {
		return <div>Loading data...</div>;
	}

	const currentImage = design.images.find((img) => img.placeholderId === selectedPlaceholder);
	function getImage(placeholderId: string): IConceptDesignImage | undefined {
		const ret = design?.images.find((img) => img.placeholderId === placeholderId);
		// logDebug("getImage called with positionId:", placeholderId, design?.images, "found image:", ret);
		return ret;
	}

	return (
		<div>
			<div className="flex gap-4 bg-red-100 w-[400px] relative" key={design.id}>
				<div className="h-[200px] overflow-y-auto flex flex-col ">
					{placeholders.map((placeholder) => (
						<div
							onClick={() => {
								setSelectedPlaceholder(placeholder.id);
							}}
							key={placeholder.id}
							className="flex items-center gap-1 cursor-pointer hover:bg-gray-200 p-0.5 rounded"
							style={{
								backgroundColor: selectedPlaceholder === placeholder.id ? "#e0f7fa" : "transparent",
								color: getImage(placeholder.id) ? "black" : "red",
							}}
						>
							<div className="text-xs">{placeholder.position}</div>
							<div className="text-xs">
								({placeholder.width}x{placeholder.height})
							</div>
							{getImage(placeholder.id) && (
								<button
									className="bg-red-500 text-white px-1 rounded"
									onClick={async () => {
										design.images = design.images.filter((img) => img.placeholderId !== placeholder.id);
										await setDoc(doc(useInAppBlueprintsRef, conceptId), { ...concept });
									}}
								>
									x
								</button>
							)}{" "}
						</div>
					))}
				</div>
				<div key={design.id} className="p-2 bg-gray-200 rounded w-[200px] h-[200px] ">
					<div className="flex justify-between items-center mb-2">
						<div className="font-bold">{design.name}</div>
						<div className="flex items-center gap-2">
							<UploadImageButton
								onImageSelected={async (fileName, base64Content) => {
									appStore.showLoading = true;
									try {
										const response = await printifyService.uploadImage(fileName, base64Content);
										const { preview_url, id, file_name } = response;
										const prev = getImage(selectedPlaceholder);
										if (prev) {
											prev.url = preview_url;
											prev.filename = file_name;
											prev.id = `${id}`;
										} else {
											design.images.push({
												placeholderId: selectedPlaceholder,
												id: `${id}`,
												url: preview_url,
												position: placeholders.find((p) => p.id === selectedPlaceholder)!.position,
												filename: file_name,
											});
										}
										await setDoc(doc(useInAppBlueprintsRef, conceptId), { ...concept });
										logDebug("Image uploaded successfully:", response);
									} catch (error) {
										logError("Error uploading image:", error);
									}
									appStore.showLoading = false;
								}}
								buttonText=""
							/>
							<button
								className="bg-red-500 text-white p-1 rounded"
								onClick={async () => {
									const areYouSure = window.confirm(
										"Are you sure you want to delete this design? ( To remove the image click on the X button next to the position )"
									);
									if (!areYouSure) return;
									conceptBlueprint.designs = conceptBlueprint.designs.filter((d) => d.id !== designId);
									await setDoc(doc(useInAppBlueprintsRef, conceptId), { ...concept });
								}}
							>
								X
							</button>
						</div>
					</div>
					<div className="flex gap-2">
						{currentImage && <PreviewImage key={currentImage.id} imageUrl={currentImage.url} cls="h-32 object-cover" />}
					</div>
				</div>
				<div
					onClick={() => {
						appStore.selectedDesign = designId;
					}}
					className={`w-8 h-8   cursor-pointer absolute right-0 top-0 flex justify-center items-center text-center ${
						appStore.selectedDesign === designId ? "bg-blue-500" : "bg-gray-300"
					}`}
					style={{
						fontSize: "9px",
					}}
				>
					Select Design
				</div>
			</div>
		</div>
	);
});

const lightColors = ["White", "Ash", "Sport Grey", "Sand", "Cornsilk", "Mint Green", "Light Blue", "Light Pink"];
const darkColors = [
	// Darks (8):
	"Graphite Heather",
	"Black",
	"Red",
	"Orange",
	"Irish Green",
	"Indigo Blue",
	"Royal",
	"Lilac",
];
const allColors = [...lightColors, ...darkColors];

const BlueprintEditor: React.FC<{ conceptId: string; blueprintId: number }> = observer(({ conceptId, blueprintId }) => {
	const firestore = useFirestore();
	const [showPreviewDesigns, setShowPreviewDesigns] = React.useState(false);
	const [colorMap, setColorMap] = React.useState<Record<string, string>>({});
	// const [colors, setColors] = React.useState<string[]>([]);
	const [collapse, setCollapse] = React.useState(false);
	const [variants, setVariants] = React.useState<IVariantSimple[]>([]);
	const useInAppBlueprintsRef = collection(firestore, "/store/site/concepts") as CollectionReference<IConcept>;
	const useInAppBlueprintsCD = useFirestoreCollectionData<IConcept>(useInAppBlueprintsRef, { idField: "id" });
	const concept = useInAppBlueprintsCD.data.find((c) => c.id === conceptId);
	// logDebug("!!!!!!!!!!", variants);
	const printifyBlueprint = appStore.selectedUKProviderBlueprints.find((bp) => bp.id === blueprintId);
	const conceptBlueprint = concept?.bluprints.find((bp) => bp.id === blueprintId);
	const fetchingProduct = React.useRef(false);
	const [product, setProduct] = React.useState<ProductType>();
	useEffect(() => {
		async function init() {
			if (fetchingProduct.current) {
				return;
			}
			const fromCache = await localLargeStorage.getItem(`${conceptId}-${blueprintId}`);
			if (fromCache) {
				const parsed = JSON.parse(fromCache);
				if (parsed.cacheTime && Date.now() - parsed.cacheTime < 1000 * 60 * 60 * 2) {
					appStore.setProduct(`${conceptId}-${blueprintId}`, JSON.parse(fromCache));
					// logDebug("Product details fetched and set from Cache:", parsed);
					setProduct(parsed);
					return;
				}
			}
			if (!appStore.getProduct(`${conceptId}-${blueprintId}`)) {
				fetchingProduct.current = true;
				// logDebug("Fetching product details for concept:", conceptId, "blueprint:", blueprintId);
				appStore.showLoading = true;
				try {
					let productDetails = await printifyService.createDummyProductAndFetchPrices(
						appStore.selectedProviderId,
						blueprintId,
						appStore.allImages[0].id
					);
					productDetails = await fixProduct(productDetails);
					setProduct(productDetails);
					appStore.setProduct(`${conceptId}-${blueprintId}`, productDetails);
					await localLargeStorage.setItem(
						`${conceptId}-${blueprintId}`,
						JSON.stringify({
							cacheTime: Date.now(),
							...productDetails,
						})
					);
					logDebug("Product details fetched and set:", productDetails);
				} catch (err) {}
				appStore.showLoading = false;
				fetchingProduct.current = false;
			}
		}
		init();
	}, []);

	useEffect(() => {
		async function fetchBlueprintData() {
			const colorMap = await getColorsMap();
			const blueprintVariants = (await appStore.get("getVariants", {
				blueprintId: blueprintId,
				providerId: appStore.selectedProviderId,
			})) as any;
			if (!blueprintVariants) {
				logError(`Failed to fetch variants for blueprint ID ${blueprintId}`);
				return;
			}
			setVariants(blueprintVariants.variants || []);
			// const colors = ((blueprintVariants.variants || []) as IVariantSimple[])
			// 	.filter((v) => v.options.color)
			// 	.map((v) => v.options.color)
			// 	.filter((color, index, self) => {
			// 		return self.indexOf(color) === index;
			// 	})
			// 	.map((c) => colorMap[c] || c);
			// logDebug("Fetched colors:", colors);
			// setColors(colors);

			setColorMap(colorMap);
			// logDebug("Fetched blueprint variants:", blueprintVariants);
		}
		fetchBlueprintData();
	}, []);

	let placeholders = variants
		.flatMap((v) => v.placeholders.map((p) => ({ ...p, id: `${p.position}-${(p.width / p.height).toFixed(2)}` })) || [])
		.filter((p, index, self) => {
			return self.findIndex((ph) => ph.id === p.id) === index;
		});
	// make sure front is always first
	placeholders = placeholders.sort((a, b) => {
		if (a.position === "front" && b.position !== "front") return -1;
		if (b.position === "front" && a.position !== "front") return 1;
		return 0;
	});
	if (!product || useInAppBlueprintsCD.status === "loading" || !concept || !conceptBlueprint || !printifyBlueprint || !variants.length) {
		return <div>Loading data...</div>;
	}

	return (
		<div className="flex flex-col gap-2 m-2 p-2">
			<div className="flex items-center">
				<div>{printifyBlueprint.title}</div>
				<PreviewImage imageUrl={printifyBlueprint.images[0]} cls="w-8 h-8 object-cover m-2" />
				<button
					className={`bg-gray-300 p-1 rounded ${collapse ? "-rotate-90" : ""}`}
					onClick={() => {
						setCollapse(!collapse);
					}}
				>
					▼
				</button>
			</div>
			{!collapse && (
				<>
					<div className="flex gap-2 overflow-x-auto">
						{conceptBlueprint.designs?.map((design) => (
							<DesignEditor
								// colorMap={colorMap}
								variants={variants}
								key={design.id}
								conceptId={conceptId}
								blueprintId={blueprintId}
								designId={design.id}
								placeholders={placeholders}
							/>
						))}
					</div>
					{product && appStore.selectedDesign && (
						<div>
							<ColorsSelector
								colorMap={colorMap}
								conceptId={conceptId}
								blueprintId={blueprintId}
								designId={appStore.selectedDesign}
								colors={(
									product.options
										?.find((opt) => opt.type === "color")
										?.values.filter((v) => v.used)
										.map((v) => v.colors?.[0] || "") || []
								).filter((c) => {
									const colorName = Object.entries(colorMap).filter(([key, value]) => value === c)[0][0];
									return allColors.includes(colorName);
								})}
								onColorSelected={() => {}}
							/>
							<OtherFilters
								conceptId={conceptId}
								blueprintId={blueprintId}
								designId={appStore.selectedDesign}
								product={product}
							/>

							<PriceAndTagsSelector
								conceptId={conceptId}
								blueprintId={blueprintId}
								designId={appStore.selectedDesign}
								product={product}
							/>
						</div>
					)}
					<div className="flex gap-2">
						<SimpleButton
							onClick={async () => {
								const designName = prompt("Enter design name:");
								if (designName) {
									const newDesign: IConceptDesign = {
										name: designName,
										id: uuidv4(),
										images: [],
									};
									const updatedConcept = {
										...concept,
										bluprints: concept.bluprints.map((bp) =>
											bp.id === blueprintId ? { ...bp, designs: [...(bp.designs || []), newDesign] } : bp
										),
									};
									await setDoc(doc(useInAppBlueprintsRef, conceptId), updatedConcept);
								}
							}}
							label="Add Design"
							className="w-32"
						/>
						<SimpleButton
							onClick={async () => {
								setShowPreviewDesigns(true);
							}}
							label="Preview Designs"
							// className="w-32"
						/>
						<SimpleButton
							onClick={async () => {
								appStore.showLoading = true;
								try {
									const payload = createProductCreationPayload(
										conceptBlueprint,
										printifyBlueprint,
										variants,
										colorMap,
										product
									);
									if (!payload) {
										throw new Error("Failed to create product payload");
									}
									logDebug("Payload for product creation:", payload);
									const finalProduct = await printifyService.createProductFromPayload(payload);
									if (!finalProduct) {
										alert("Failed to create product. Please check the console for details.");
										logError("Failed to create product. Payload:", payload);
									} else {
										const id = finalProduct.id;
										conceptBlueprint.storedProductId = id;
										setDoc(doc(useInAppBlueprintsRef, conceptId), { ...concept });
									}
								} catch (err: any) {
									alert(err.message || "Error creating product");
									logError("Error creating product:", err);
								}
								appStore.showLoading = false;
								alert("Product created successfully! Check your Printify account.");
							}}
							label="Create Product"
							// className="w-32"
						/>
					</div>
				</>
			)}
			{showPreviewDesigns && (
				<TopMostPortal>
					<div className="relative">
						<div
							className="absolute top-0 right-0 bg-gray-500 text-white p-2 cursor-pointer"
							onClick={(e) => {
								setShowPreviewDesigns(false);
							}}
							style={{ zIndex: 1000 }}
						>
							X
						</div>
						<PreviewPage
							onClose={() => {
								setShowPreviewDesigns(false);
							}}
							printiryRefProduct={product}
							colorMap={colorMap}
							variants={variants}
							printifyBlueprint={printifyBlueprint}
							conceptBlueprint={conceptBlueprint}
						/>
					</div>
				</TopMostPortal>
			)}{" "}
		</div>
	);
});

const ConceptEditor: React.FC<{ conceptId: string }> = observer(({ conceptId }) => {
	const [showBlueprintSelector, setShowBlueprintSelector] = React.useState(false);
	const firestore = useFirestore();
	const useInAppBlueprintsRef = collection(firestore, "/store/site/concepts") as CollectionReference<IConcept>;
	const useInAppBlueprintsCD = useFirestoreCollectionData<IConcept>(useInAppBlueprintsRef, { idField: "id" });
	const concept = useInAppBlueprintsCD.data.find((c) => c.id === conceptId);
	if (useInAppBlueprintsCD.status === "loading" || !concept) {
		return <div>Loading concept...</div>;
	}
	// if (concept.bluprints?.length > 0) {
	// 	logDebug(
	// 		"bp",
	// 		appStore.selectedUKProviderBlueprints.find((bp) => concept.bluprints?.find((b) => b.id === bp.id))
	// 	);
	// }
	return (
		<div className="p-4">
			<SimpleButton
				onClick={async () => {
					setShowBlueprintSelector(true);
				}}
				label="Add printify blueprint"
				className="m-2"
			/>
			<div className="h-full overflow-y-auto">
				{concept.bluprints?.map((bp) => (
					<BlueprintEditor key={bp.id} conceptId={conceptId} blueprintId={bp.id} />
				))}
			</div>

			{showBlueprintSelector && (
				<TopMostPortal>
					<div className="p-4 h-[50%] w-[90%] bg-gray-100 relative">
						<div
							className="absolute top-0 right-0 bg-gray-500 text-white p-2 cursor-pointer"
							onClick={(e) => {
								setShowBlueprintSelector(false);
							}}
							style={{ zIndex: 1000 }}
						>
							X
						</div>
						<div className=" overflow-y-auto h-full">
							{appStore.selectedUKProviderBlueprints.map((bp) => (
								<div
									key={bp.id}
									className="m-2 cursor-pointer hover:bg-gray-200 p-2 rounded whitespace-nowrap"
									onClick={async () => {
										appStore.showLoading = true;
										try {
											const updatedConcept = { ...concept, bluprints: [...(concept.bluprints || []), { id: bp.id }] };
											await setDoc(doc(useInAppBlueprintsRef, conceptId), updatedConcept);
											setShowBlueprintSelector(false);
										} catch (err) {}
										appStore.showLoading = false;
									}}
								>
									{bp.title}
									<PreviewImage imageUrl={bp.images[0]} cls="w-16 h-16 object-cover" />
								</div>
							))}
						</div>
					</div>
				</TopMostPortal>
			)}
		</div>
	);
});

export const ConceptsPage: React.FC<{}> = observer(({}) => {
	const firestore = useFirestore();
	const [selectedConcept, setSelectedConcept] = React.useState<string | null>(null);
	const conceptsRef = collection(firestore, "/store/site/concepts") as CollectionReference<IConcept>;
	const conceptsCD = useFirestoreCollectionData<IConcept>(conceptsRef, { idField: "id" });
	if (conceptsCD.status === "loading") {
		return <div>Loading concepts...</div>;
	}
	return (
		<div>
			Concepts
			<SimpleButton
				onClick={async () => {
					const conceptName = prompt("Enter new concept name:");
					if (conceptName) {
						const newConcept = { name: conceptName }; // Adjust the structure as needed
						const docRef = doc(conceptsRef, uuidv4());
						try {
							await setDoc(docRef, newConcept);
						} catch (err) {}
					}
				}}
				label="New Concept"
				className="m-2"
			/>
			<div className="flex justify-between h-[80vh]">
				<div className="flex flex-col w-64 h-full bg-red-100">
					{conceptsCD.data.map((concept) => (
						<div
							key={concept.id}
							className="m-2 cursor-pointer hover:bg-gray-200 p-2 rounded whitespace-nowrap"
							style={{
								backgroundColor: selectedConcept === concept.id ? "#e0f7fa" : "transparent",
							}}
							onClick={async () => {
								setSelectedConcept(concept.id || null);
							}}
						>
							{concept.name}
						</div>
					))}
				</div>
				<div key={selectedConcept} className="w-full bg-blue-100 h-[80vh] overflow-y-auto">
					{selectedConcept && <ConceptEditor conceptId={selectedConcept} />}
				</div>
			</div>
		</div>
	);
});
